import { Fragment, useState } from "react";
import { Search, Package, Sprout, ChevronRight, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useUnifiedStoreInventory } from "../hooks/useStoreInventory";
import { useMovementsByInput } from "../hooks/useStockMovement";
import { useInputBatches } from "../hooks/useInputBatches";
import { useCreateInputDiscard } from "../hooks/useInputDiscard";
import { useSeedBatch } from "@/features/production/hooks/useSeedBatches";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useNotification } from "@/shared/hooks/useNotification";
import { Checkbox } from "@/components/ui/checkbox";
import { INPUT_DISCARD_REASONS } from "../services/inputDiscardApi";
import type { StockMovement } from "../services/stockMovementsApi";

export const StoreInventoryPage = () => {
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [batchPageNumber, setBatchPageNumber] = useState(1);
  const [batchPageSize, setBatchPageSize] = useState(20);
  const [expandedInputId, setExpandedInputId] = useState<string | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  const { data, isLoading } = useUnifiedStoreInventory({
    search: search || undefined,
    includeCustomerSeeds: true,
    pageNumber,
    pageSize,
    batchPageNumber,
    batchPageSize,
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageNumber(1);
    setBatchPageNumber(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const handleBatchPageSizeChange = (size: number) => {
    setBatchPageSize(size);
    setBatchPageNumber(1);
  };

  const toggleInput = (id: string) =>
    setExpandedInputId((prev) => (prev === id ? null : id));
  const toggleBatch = (id: string) =>
    setExpandedBatchId((prev) => (prev === id ? null : id));


  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Store Inventory</h1>
          <p className="text-muted-foreground mt-1">
            All physical stock — company inventory and customer seed batches
          </p>
        </div>

        {/* Summary cards */}
        {data?.summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Company Items</CardDescription>
                <CardTitle className="text-2xl">{data.summary.totalCompanyItems}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Customer Seed Batches</CardDescription>
                <CardTitle className="text-2xl">{data.summary.totalCustomerBatches}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Low Stock Alerts</CardDescription>
                <CardTitle className="text-2xl text-destructive">{data.summary.lowStockAlerts}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, code, batch, or customer..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && <p className="text-muted-foreground">Loading inventory...</p>}

        {/* Company Inventory */}
        {data && (data.companyInventory ?? []).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-lg">Company Inventory</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Input</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Current Stock</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.companyInventory ?? []).map((item) => {
                    const isOpen = expandedInputId === item.inputId;
                    return (
                      <Fragment key={item.inputId}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => toggleInput(item.inputId)}
                        >
                          <TableCell className="w-8 p-2">
                            <ChevronRight
                              className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{item.inputName}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{item.inputCode}</TableCell>
                          <TableCell className="text-sm">{item.category ?? "—"}</TableCell>
                          <TableCell className="text-right font-mono">
                            {item.currentStock}
                            {item.totalSeedCount != null && (
                              <div className="text-xs text-muted-foreground font-normal">
                                {Number(item.totalSeedCount).toLocaleString()} seeds
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{item.unitOfMeasure}</TableCell>
                          <TableCell>
                            {item.isLowStock ? (
                              <Badge variant="destructive">Low Stock</Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-700 border-green-300">OK</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={7} className="py-4">
                              <CompanyInputBreakdown
                                inputId={item.inputId}
                                unit={item.unitOfMeasure}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Pagination for Company Inventory */}
        {data && data.summary.totalCompanyItems > 0 && (
          <PaginationBar
            currentPage={data.currentPage}
            pageSize={data.pageSize}
            totalCount={data.summary.totalCompanyItems}
            totalPages={data.totalPages}
            onPageChange={setPageNumber}
            onPageSizeChange={handlePageSizeChange}
          />
        )}

        {/* Customer Seed Batches */}
        {data && (data.customerSeedBatches ?? []).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Sprout className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-lg">Customer Seed Batches</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Batch #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Crop / Variety</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.customerSeedBatches ?? []).map((batch) => {
                    const isOpen = expandedBatchId === batch.seedBatchId;
                    return (
                      <Fragment key={batch.seedBatchId}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => toggleBatch(batch.seedBatchId)}
                        >
                          <TableCell className="w-8 p-2">
                            <ChevronRight
                              className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{batch.batchNumber}</TableCell>
                          <TableCell>{batch.customerName}</TableCell>
                          <TableCell className="text-sm">
                            {batch.cropType}
                            {batch.variety && <span className="text-muted-foreground"> — {batch.variety}</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono">{batch.quantityAvailable}</TableCell>
                          <TableCell className="text-sm">{batch.unit}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{batch.storageLocation ?? "—"}</TableCell>
                          <TableCell className="text-sm">{new Date(batch.receivedDate).toLocaleDateString()}</TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={8} className="py-4">
                              <SeedBatchBreakdown id={batch.seedBatchId} unit={batch.unit} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Pagination for Customer Seed Batches */}
        {data && data.summary.totalCustomerBatches > 0 && (
          <PaginationBar
            currentPage={data.batchCurrentPage}
            pageSize={data.batchPageSize}
            totalCount={data.summary.totalCustomerBatches}
            totalPages={data.batchTotalPages}
            onPageChange={setBatchPageNumber}
            onPageSizeChange={handleBatchPageSizeChange}
          />
        )}

        {data && (data.companyInventory ?? []).length === 0 && (data.customerSeedBatches ?? []).length === 0 && (
          <p className="text-muted-foreground text-center py-8">No inventory items found.</p>
        )}
      </div>
    </div>
  );
};

// --- Expanded breakdown panels ---------------------------------------------

const Stat = ({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: string | number;
  unit?: string;
  tone?: "neutral" | "issued" | "returned" | "reserved" | "sown" | "available";
}) => {
  const toneClass =
    tone === "issued"
      ? "text-blue-700"
      : tone === "returned"
        ? "text-emerald-700"
        : tone === "reserved"
          ? "text-amber-700"
          : tone === "sown"
            ? "text-blue-700"
            : tone === "available"
              ? "text-emerald-700"
              : "text-foreground";
  return (
    <div className="rounded-md border bg-background px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>
        {value}
        {unit && <span className="ml-1 text-xs font-normal text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
};

// --- Request Discard dialog ------------------------------------------------

interface RequestDiscardDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  inputId: string;
  unit: string;
}

const RequestDiscardDialog = ({ open, onOpenChange, inputId, unit }: RequestDiscardDialogProps) => {
  const notify = useNotification();
  const batchQuery = useInputBatches(inputId);
  const createMutation = useCreateInputDiscard();

  const [batchId, setBatchId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [discardAll, setDiscardAll] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [detailsTouched, setDetailsTouched] = useState(false);

  const MIN_DETAILS_LENGTH = 10;

  const reset = () => {
    setBatchId("");
    setQuantity("");
    setDiscardAll(false);
    setReason("");
    setDetails("");
    setDetailsTouched(false);
  };

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const batches = (batchQuery.data ?? []).filter((b) => b.currentQuantity > 0 && b.isActive && !b.isExpired);
  const selectedBatch = batches.find((b) => b.id === batchId);
  const maxQty = selectedBatch?.currentQuantity ?? 0;

  const handleBatchChange = (id: string) => {
    setBatchId(id);
    setDiscardAll(false);
    setQuantity("");
  };

  const handleDiscardAllToggle = (checked: boolean) => {
    setDiscardAll(checked);
    if (checked && maxQty > 0) setQuantity(String(maxQty));
    else setQuantity("");
  };

  const handleSubmit = () => {
    const qty = parseFloat(quantity);
    setDetailsTouched(true);
    if (!batchId || !qty || qty <= 0 || !reason || details.trim().length < MIN_DETAILS_LENGTH) return;
    createMutation.mutate(
      { inputBatchId: batchId, quantityToDiscard: qty, reason, reasonDetails: details, requiresApproval: true },
      {
        onSuccess: () => {
          notify.success("Discard request submitted — awaiting approval");
          handleClose(false);
        },
        onError: () => notify.error("Failed to submit discard request"),
      }
    );
  };

  const qty = parseFloat(quantity);
  const detailsError = detailsTouched && details.trim().length < MIN_DETAILS_LENGTH
    ? `Please provide at least ${MIN_DETAILS_LENGTH} characters (${details.trim().length}/${MIN_DETAILS_LENGTH})`
    : null;
  const isValid = !!batchId && qty > 0 && qty <= maxQty && !!reason && details.trim().length >= MIN_DETAILS_LENGTH;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Discard</DialogTitle>
          <DialogDescription>
            Submit a discard request for damaged or expired stock. A manager must approve before stock is written off.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor="batch-select">Batch</FieldLabel>
            {batchQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading batches…</p>
            ) : batches.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active batches with available stock.</p>
            ) : (
              <Select value={batchId} onValueChange={handleBatchChange}>
                <SelectTrigger id="batch-select">
                  <SelectValue placeholder="Select batch…" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map((b) => {
                    const qty = b.currentQuantity;
                    return (
                      <SelectItem key={b.id} value={b.id}>
                        {b.batchNumber} — {qty} {unit} available
                        {b.expiryDate && ` · Exp ${new Date(b.expiryDate).toLocaleDateString()}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field>
            <FieldLabel htmlFor="discard-qty">
              Quantity to Discard {selectedBatch && `(max ${maxQty} ${unit})`}
            </FieldLabel>
            <Input
              id="discard-qty"
              type="number"
              min={1}
              max={maxQty || undefined}
              step="any"
              placeholder={batchId ? `e.g. 10` : "Select a batch first"}
              value={quantity}
              onChange={(e) => { setDiscardAll(false); setQuantity(e.target.value); }}
              disabled={discardAll}
            />
            {batchId && (
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer select-none">
                <Checkbox
                  id="discard-all"
                  checked={discardAll}
                  onCheckedChange={(v) => handleDiscardAllToggle(!!v)}
                />
                <span className="text-sm text-muted-foreground">
                  Discard entire batch ({maxQty} {unit})
                </span>
              </label>
            )}
          </Field>
          <Field>
            <FieldLabel htmlFor="discard-reason">Reason</FieldLabel>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="discard-reason">
                <SelectValue placeholder="Select reason…" />
              </SelectTrigger>
              <SelectContent>
                {INPUT_DISCARD_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="discard-details">
              Details <span className="text-destructive">*</span>
            </FieldLabel>
            <Textarea
              id="discard-details"
              rows={3}
              placeholder="Describe the condition of the stock — e.g. bags torn, contaminated with moisture…"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              onBlur={() => setDetailsTouched(true)}
              className={detailsError ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {detailsError ? (
              <p className="text-xs text-destructive mt-1">{detailsError}</p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                Minimum {MIN_DETAILS_LENGTH} characters required.
              </p>
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || createMutation.isPending}>
            {createMutation.isPending ? "Submitting…" : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Company input breakdown panel -----------------------------------------

const CompanyInputBreakdown = ({ inputId, unit }: { inputId: string; unit: string }) => {
  const { data, isLoading } = useMovementsByInput(inputId);
  const [discardOpen, setDiscardOpen] = useState(false);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground px-1">Loading movement history…</p>;
  }

  const movements = data ?? [];
  const sum = (type: StockMovement["movementType"]) =>
    movements.filter((m) => m.movementType === type).reduce((acc, m) => acc + Number(m.quantity || 0), 0);

  const totalIssued = sum("Issue");
  const totalReturned = sum("Return");
  const netUsed = Math.max(totalIssued - totalReturned, 0);
  const returnRatio = totalIssued > 0 ? Math.min(totalReturned / totalIssued, 1) : 0;

  const recent = [...movements]
    .filter((m) => m.movementType === "Issue" || m.movementType === "Return")
    .sort((a, b) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime())
    .slice(0, 5);

  return (
    <div className="space-y-3 px-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <Stat label="Total issued" value={totalIssued} unit={unit} tone="issued" />
        <Stat label="Total returned" value={totalReturned} unit={unit} tone="returned" />
        <Stat label="Net consumed" value={netUsed} unit={unit} />
      </div>

      {totalIssued > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>Issued vs. returned</span>
            <span>{Math.round(returnRatio * 100)}% returned</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-blue-100">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${returnRatio * 100}%` }}
              title={`${totalReturned} ${unit} returned of ${totalIssued} ${unit} issued`}
            />
          </div>
        </div>
      )}

      {recent.length > 0 ? (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            Recent activity
          </div>
          <ul className="divide-y rounded-md border bg-background">
            {recent.map((m) => (
              <li key={m.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                <span className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={
                      m.movementType === "Issue"
                        ? "border-blue-200 text-blue-700"
                        : "border-emerald-200 text-emerald-700"
                    }
                  >
                    {m.movementType}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {new Date(m.movementDate).toLocaleDateString()}
                  </span>
                  {m.referenceNumber && (
                    <span className="text-muted-foreground text-xs">· {m.referenceNumber}</span>
                  )}
                </span>
                <span className="font-mono text-sm">
                  {Number(m.quantity).toLocaleString()} <span className="text-muted-foreground text-xs">{unit}</span>
                </span>
              </li>
            ))}
          </ul>
          {movements.filter((m) => m.movementType === "Issue" || m.movementType === "Return").length > recent.length && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              Showing 5 most recent issues/returns
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No issues or returns recorded yet.</p>
      )}

      <PermissionGate permissions={["discard_requests.create"]}>
        <div className="pt-1 border-t">
          <Button
            size="sm"
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => setDiscardOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Request Discard
          </Button>
        </div>
      </PermissionGate>

      <RequestDiscardDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        inputId={inputId}
        unit={unit}
      />
    </div>
  );
};

// --- Request Seed Discard dialog ------------------------------------------

const MIN_DETAILS_LENGTH = 10;

interface RequestSeedDiscardDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  seedBatchId: string;
  batchNumber: string;
  availableQty: number;
  unit: string;
}

const RequestSeedDiscardDialog = ({
  open,
  onOpenChange,
  seedBatchId,
  batchNumber,
  availableQty,
  unit,
}: RequestSeedDiscardDialogProps) => {
  const [quantity, setQuantity] = useState("");
  const [discardAll, setDiscardAll] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [detailsTouched, setDetailsTouched] = useState(false);
  const { mutateAsync, isPending } = useCreateInputDiscard();
  const notify = useNotification();

  const qty = Number(quantity);
  const isValid =
    qty > 0 &&
    qty <= availableQty &&
    !!reason &&
    details.trim().length >= MIN_DETAILS_LENGTH;

  const handleClose = () => {
    setQuantity("");
    setDiscardAll(false);
    setReason("");
    setDetails("");
    setDetailsTouched(false);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await mutateAsync({
        seedBatchId,
        quantityToDiscard: qty,
        reason,
        reasonDetails: details.trim(),
      });
      notify.success("Discard request submitted — awaiting approval");
      handleClose();
    } catch {
      notify.error("Failed to submit discard request");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-destructive" />
            Request Seed Discard
          </DialogTitle>
          <DialogDescription>
            Batch <strong>{batchNumber}</strong> — {availableQty} {unit} available
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quantity */}
          <Field>
            <FieldLabel>Quantity to Discard</FieldLabel>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={availableQty}
                value={quantity}
                disabled={discardAll}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  if (discardAll) setDiscardAll(false);
                }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm disabled:opacity-50"
                placeholder={`Max ${availableQty}`}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">{unit}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Checkbox
                id="seed-discard-all"
                checked={discardAll}
                onCheckedChange={(checked) => {
                  const v = !!checked;
                  setDiscardAll(v);
                  if (v) setQuantity(String(availableQty));
                }}
              />
              <label htmlFor="seed-discard-all" className="text-sm cursor-pointer">
                Discard entire batch ({availableQty} {unit})
              </label>
            </div>
            {qty > availableQty && (
              <p className="text-xs text-destructive mt-1">Exceeds available quantity</p>
            )}
          </Field>

          {/* Reason */}
          <Field>
            <FieldLabel>Reason</FieldLabel>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {INPUT_DISCARD_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Details */}
          <Field>
            <FieldLabel>
              Details <span className="text-destructive">*</span>
            </FieldLabel>
            <Textarea
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              onBlur={() => setDetailsTouched(true)}
              placeholder="Describe the condition or reason in detail (required)"
            />
            {detailsTouched && details.trim().length < MIN_DETAILS_LENGTH && (
              <p className="text-xs text-destructive mt-1">
                Please provide at least {MIN_DETAILS_LENGTH} characters
              </p>
            )}
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending ? "Submitting…" : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SeedBatchBreakdown = ({ id, unit }: { id: string; unit: string }) => {
  const { data, isLoading } = useSeedBatch(id);
  const [discardOpen, setDiscardOpen] = useState(false);

  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground px-1">Loading batch details…</p>;
  }

  const received = Number(data.quantityReceived || 0);
  const reserved = Number(data.quantityReserved || 0);
  const sown = Number(data.quantitySown || 0);
  const returned = Number(data.quantityReturned || 0);
  const discarded = Number(data.quantityDiscarded || 0);
  const available = Number(data.quantityAvailable ?? received - reserved - sown - returned - discarded);

  const pct = (n: number) => (received > 0 ? (n / received) * 100 : 0);

  return (
    <div className="space-y-3 px-1">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total received</div>
          <div className="text-lg font-semibold tabular-nums">
            {received.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">{unit}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data.numberOfPacks > 0 && data.quantityPerPack > 0 && (
            <div className="text-xs text-muted-foreground">
              {data.numberOfPacks} pack{data.numberOfPacks > 1 ? "s" : ""} × {data.quantityPerPack} {unit}
            </div>
          )}
          <PermissionGate permissions={["discard_requests.create"]}>
            <Button
              size="sm"
              variant="outline"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={(e) => { e.stopPropagation(); setDiscardOpen(true); }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Request Discard
            </Button>
          </PermissionGate>
        </div>
      </div>

      {received > 0 && (
        <div className="space-y-1">
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="bg-amber-400" style={{ width: `${pct(reserved)}%` }} title={`Reserved ${reserved} ${unit}`} />
            <div className="bg-blue-500" style={{ width: `${pct(sown)}%` }} title={`Sown ${sown} ${unit}`} />
            <div className="bg-rose-400" style={{ width: `${pct(returned)}%` }} title={`Returned ${returned} ${unit}`} />
            <div className="bg-red-600" style={{ width: `${pct(discarded)}%` }} title={`Discarded ${discarded} ${unit}`} />
            <div className="bg-emerald-500" style={{ width: `${pct(available)}%` }} title={`Available ${available} ${unit}`} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <LegendDot color="bg-amber-400" label={`Reserved ${reserved}`} />
            <LegendDot color="bg-blue-500" label={`Sown ${sown}`} />
            <LegendDot color="bg-rose-400" label={`Returned ${returned}`} />
            {discarded > 0 && <LegendDot color="bg-red-600" label={`Discarded ${discarded}`} />}
            <LegendDot color="bg-emerald-500" label={`Available ${available}`} />
          </div>
        </div>
      )}

      <div className={`grid gap-2 ${discarded > 0 ? "grid-cols-5" : "grid-cols-4"}`}>
        <Stat label="Reserved" value={reserved} unit={unit} tone="reserved" />
        <Stat label="Sown" value={sown} unit={unit} tone="sown" />
        <Stat label="Returned" value={returned} unit={unit} tone="returned" />
        {discarded > 0 && <Stat label="Discarded" value={discarded} unit={unit} tone="issued" />}
        <Stat label="Available" value={available} unit={unit} tone="available" />
      </div>

      <RequestSeedDiscardDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        seedBatchId={id}
        batchNumber={data.batchNumber ?? id}
        availableQty={available}
        unit={unit}
      />
    </div>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
    {label}
  </span>
);
